import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useWatch } from "react-hook-form";
import { ModernSheet } from "components/common/ModernSheet/ModernSheet";
import { Modal, ModalHeader } from "components/common/modal";
import { Button } from "components/common/buttons/Button";
import { useCreateProjectPermissionGuard } from "components/common/CreateProjectPermissionGuard";
import { useFileUpload } from "components/common/CreateProjectSheet/CreateProjectSheet.hooks";
import { suggestProjectName } from "components/common/CreateProjectSheet/CreateProjectSheet.utils";
import { useBackgroundTasks } from "providers/background-tasks";
import {
  PermissionAlerts,
  LoadingState,
  PermissionDeniedState,
} from "components/common/CreateProjectSheet/components";
import { LoaderV2, LoaderMode, LoaderTheme } from "components/common/LoaderV2/LoaderV2";
import { FileText } from "lucide-react";
import { SourcePicker } from "./components/SourcePicker";
import { DetailsStage, ImportStage } from "./stages";
import { useCreateProjectForm } from "hooks/project/useCreateProjectForm";
import { useWorkspaceIdentifiers } from "hooks/useWorkspace/useWorkspaceIdentifiers";
import { useOneTimePurchase } from "store/hooks/useOneTimePurchase";
import type { ProjectDataType } from "store/types/project";
import type { FormData } from "components/common/CreateProjectSheet/CreateProjectSheet.types";
import type { ProjectCreateSource } from "./types/project-create-source.types";
import { useAnalytics } from "hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "analytics";
import { handleToast } from "utils/toasts";
import { useOptionalUserPreferences } from "providers/user-preferences";
import {
  finalDraftTagsFromParse,
  type HeldOffFeatures,
  type ParsedFinalDraftTags,
} from "services/finalDraftTags";

const NO_FINAL_DRAFT_TAGS: ParsedFinalDraftTags = { source: 0, kept: 0 };
/** Script breakdown and the features that need it. */
const BREAKDOWN_KEYS = ["has_script_breakdown", "has_budget", "has_schedule"] as const;

const stageTexts: Record<string, string> = {
  uploading: "Uploading your screenplay...",
  parsing: "Analyzing script structure...",
  creating: "Creating project structure...",
  finalizing: "Finalizing project settings...",
};

export type CreateProjectWizardProps = {
  isActive: boolean;
  onClose: () => void;
  onSuccess?: (project: ProjectDataType) => void;
  existingProject?: Partial<ProjectDataType>;
  mode?: "project" | "version";
  workspaceId?: string | null;
  initialSource?: ProjectCreateSource;
  /** Screenplay picked before the wizard opened; parsed as soon as it activates. */
  initialFile?: File | null;
  entrySource?: "onboarding" | "projects" | "sidebar";
};

type Stage = "source" | "form";

export const CreateProjectWizard = ({
  isActive,
  onClose,
  onSuccess,
  existingProject,
  mode = "project",
  workspaceId: workspaceIdProp,
  initialSource,
  initialFile,
  entrySource,
}: CreateProjectWizardProps) => {
  const { workspaceId: workspaceFromHook } = useWorkspaceIdentifiers();
  const workspaceId = workspaceIdProp ?? workspaceFromHook ?? undefined;

  const isVersionMode = mode === "version";
  const [stage, setStage] = useState<Stage>(() =>
    isVersionMode || initialSource ? "form" : "source"
  );
  const [source, setSource] = useState<ProjectCreateSource | null>(() =>
    isVersionMode ? "import" : (initialSource ?? null)
  );

  const { file, fileLoading, fileError, parsedData, handleFileUpload, resetFileState } =
    useFileUpload(workspaceId ?? undefined);

  const { createProjectTasks, startCreateProject, releaseCreateProjectTask } = useBackgroundTasks();
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const activeTask = activeTaskId
    ? createProjectTasks.find((t) => t.id === activeTaskId)
    : undefined;
  const isCreatePending = activeTask?.status === "running";

  const { permissions, canCreateProject } = useCreateProjectPermissionGuard(existingProject);
  const { availableLicenses } = useOneTimePurchase();

  const otpDraftWithoutAvailableLicense =
    mode === "project" && Boolean(permissions.isOneTimePurchase) && availableLicenses === 0;

  const [isGateRedirect, setIsGateRedirect] = useState(false);
  const hasInitialized = useRef(false);
  const analyticsOpenedRef = useRef(false);
  // Name typed before switching import -> blank; the source change resets the form.
  const carriedNameRef = useRef<string | null>(null);
  const previousFormModeRef = useRef(mode);
  const modeResetRequiredRef = useRef(false);

  const { methods, defaultValues } = useCreateProjectForm({
    mode,
    existingProject,
    projectCreateSource: source,
    otpDraftWithoutAvailableLicense,
  });
  const {
    control,
    formState: { errors, isSubmitting },
  } = methods;

  const { trackEvent } = useAnalytics();
  const preferencesContext = useOptionalUserPreferences();
  const patchPreferences = preferencesContext?.patchPreferences;

  const continueInBackground = useCallback(() => {
    if (activeTaskId) {
      releaseCreateProjectTask(activeTaskId);
      trackEvent(ANALYTICS_EVENTS.PROJECT_CREATE_BACKGROUNDED, {
        create_source: source ?? (isVersionMode ? "import" : "blank"),
        mode,
      });
    }
    onClose();
  }, [activeTaskId, isVersionMode, mode, onClose, releaseCreateProjectTask, source, trackEvent]);

  const handleRequestClose = useCallback(() => {
    if (isCreatePending) {
      // Closing no longer aborts: the task keeps running in the background.
      continueInBackground();
      return;
    }
    trackEvent(ANALYTICS_EVENTS.CREATE_PROJECT_SHEET_CLOSED, { mode, stage });
    onClose();
  }, [continueInBackground, isCreatePending, mode, onClose, stage, trackEvent]);

  // Wizard unmounted while a create is running (route change, parent teardown):
  // detach so completion is reported via toasts instead of a dead callback.
  const releaseOnUnmountRef = useRef<{ taskId: string | null; running: boolean }>({
    taskId: null,
    running: false,
  });
  releaseOnUnmountRef.current = { taskId: activeTaskId, running: Boolean(isCreatePending) };
  useEffect(() => {
    return () => {
      const { taskId, running } = releaseOnUnmountRef.current;
      if (taskId && running) {
        releaseCreateProjectTask(taskId);
      }
    };
  }, [releaseCreateProjectTask]);

  useEffect(() => {
    if (!isActive) {
      analyticsOpenedRef.current = false;
      return;
    }
    if (analyticsOpenedRef.current) {
      return;
    }
    analyticsOpenedRef.current = true;
    trackEvent(ANALYTICS_EVENTS.PROJECT_CREATE_STARTED, { mode, surface: "sheet" });
    if (mode === "project" && entrySource === "onboarding") {
      trackEvent(ANALYTICS_EVENTS.PROJECT_SETUP_STARTED, {
        entry_source: entrySource,
        workspace_id: workspaceId,
      });
      if (initialSource) {
        trackEvent(ANALYTICS_EVENTS.PROJECT_SETUP_SOURCE_SELECTED, {
          source: initialSource,
          entry_source: entrySource,
          workspace_id: workspaceId,
        });
        patchPreferences?.({
          onboarding: {
            dismissed: false,
            projectSetupStatus: "source_selected",
            projectSetupSource: initialSource,
            entrySource,
          },
        });
      }
    }
    trackEvent(ANALYTICS_EVENTS.CREATE_PROJECT_SHEET_OPENED, { mode });
  }, [entrySource, initialSource, isActive, mode, patchPreferences, trackEvent, workspaceId]);

  useEffect(() => {
    if (!isActive) {
      setIsGateRedirect(false);
      hasInitialized.current = false;
      carriedNameRef.current = null;
      previousFormModeRef.current = mode;
      modeResetRequiredRef.current = false;
      setStage(isVersionMode || initialSource ? "form" : "source");
      setSource(isVersionMode ? "import" : (initialSource ?? null));
      return;
    }

    if (!permissions.isLoading && !isCreatePending && !canCreateProject && mode === "project") {
      setIsGateRedirect(true);
      onClose();
      return;
    }

    if (!hasInitialized.current) {
      methods.reset(defaultValues);
      previousFormModeRef.current = mode;
      modeResetRequiredRef.current = false;
      resetFileState();
      hasInitialized.current = true;
    }
  }, [
    isActive,
    permissions.isLoading,
    canCreateProject,
    mode,
    onClose,
    defaultValues,
    methods.reset,
    resetFileState,
    isVersionMode,
    initialSource,
    isCreatePending,
  ]);

  // Declared after the init effect so its resetFileState() runs first.
  const consumedInitialFileRef = useRef<File | null>(null);
  useEffect(() => {
    if (!isActive) {
      consumedInitialFileRef.current = null;
      return;
    }
    if (!initialFile || !hasInitialized.current) return;
    if (consumedInitialFileRef.current === initialFile) return;
    consumedInitialFileRef.current = initialFile;
    handleFileUpload([initialFile], [], mode, existingProject);
  }, [existingProject, handleFileUpload, initialFile, isActive, mode]);

  // Name a new project after its screenplay once parsed, unless a name was typed.
  // Once per file, so clearing the field does not bring the name back.
  const namedFromFileRef = useRef<File | null>(null);
  useEffect(() => {
    if (mode !== "project" || !file || !parsedData) return;
    if (namedFromFileRef.current === file) return;
    namedFromFileRef.current = file;
    if (methods.getValues("name")?.trim()) return;
    const suggestion = suggestProjectName(parsedData.title, file.name);
    if (suggestion) methods.setValue("name", suggestion, { shouldValidate: true });
  }, [file, methods, mode, parsedData]);

  // A new version from a file with Final Draft tags starts with Script breakdown off, so AI does
  // not tag it again unless asked. Budget and schedule need the breakdown, so they start off too.
  // These are version settings; the project keeps its features, so Run AI still offers them.
  // New projects keep their defaults: their choices become the project's features, and turning
  // them off would also hide those pipelines from Run AI later. Their helper text says the
  // Final Draft tags stay. Skipped when the user already changed any of the three; a later file
  // without tags restores the defaults. A form reset (entitlements refresh) applies them again.
  const finalDraftTags = file ? finalDraftTagsFromParse(parsedData) : NO_FINAL_DRAFT_TAGS;
  const finalDraftTagCount = finalDraftTags.kept;
  const finalDraftDefaultsRef = useRef<{ file: File | null; applied: boolean }>({
    file: null,
    applied: false,
  });
  // The features that are off only because Script breakdown started off; null when none are.
  const [heldOffFeatures, setHeldOffFeatures] = useState<HeldOffFeatures | null>(null);
  const applyFinalDraftDefaults = useCallback(
    (afterFormReset: boolean) => {
      if (!isVersionMode || !file || !parsedData) return;
      const tracked = finalDraftDefaultsRef.current;
      if (!afterFormReset && tracked.file === file) return;
      const wasApplied = !afterFormReset && tracked.applied;
      finalDraftDefaultsRef.current = { file, applied: false };
      setHeldOffFeatures(null);

      const touched = BREAKDOWN_KEYS.some(
        (key) => methods.getFieldState(`features.${key}`).isDirty
      );
      if (touched) return;

      if (finalDraftTagCount > 0) {
        for (const key of BREAKDOWN_KEYS) methods.setValue(`features.${key}`, false);
        finalDraftDefaultsRef.current = { file, applied: true };
        const { has_budget: budget, has_schedule: schedule } = defaultValues.features;
        setHeldOffFeatures(budget || schedule ? { budget, schedule } : null);
      } else if (wasApplied) {
        for (const key of BREAKDOWN_KEYS) {
          methods.setValue(`features.${key}`, defaultValues.features[key]);
        }
      }
    },
    [defaultValues, file, finalDraftTagCount, isVersionMode, methods, parsedData]
  );
  const applyFinalDraftDefaultsRef = useRef(applyFinalDraftDefaults);
  applyFinalDraftDefaultsRef.current = applyFinalDraftDefaults;
  useEffect(() => applyFinalDraftDefaults(false), [applyFinalDraftDefaults]);

  // Turning Script breakdown back on brings Budget and Schedule back to their defaults, unless
  // the user turned one of them on first (which is what turned the breakdown on).
  const hasScriptBreakdown = useWatch({
    control: methods.control,
    name: "features.has_script_breakdown",
  });
  useEffect(() => {
    if (!heldOffFeatures || !hasScriptBreakdown) return;
    setHeldOffFeatures(null);
    const { has_budget: budget, has_schedule: schedule } = methods.getValues("features");
    if (budget || schedule) return;
    methods.setValue("features.has_budget", defaultValues.features.has_budget);
    methods.setValue("features.has_schedule", defaultValues.features.has_schedule);
  }, [defaultValues, heldOffFeatures, hasScriptBreakdown, methods]);

  useEffect(() => {
    if (previousFormModeRef.current !== mode) {
      // Remember the boundary even when a running task defers the reset.
      modeResetRequiredRef.current = true;
    }
    previousFormModeRef.current = mode;
    if (isActive && hasInitialized.current && !isCreatePending) {
      const isContinuingProjectDraft = mode === "project" && !modeResetRequiredRef.current;
      const carriedName = carriedNameRef.current;
      carriedNameRef.current = null;
      const editedName =
        isContinuingProjectDraft && methods.getFieldState("name").isDirty
          ? methods.getValues("name")
          : undefined;
      const nameToPreserve = isContinuingProjectDraft ? (carriedName ?? editedName) : undefined;
      // Refresh entitlement defaults, then restore only the user's name edit.
      methods.reset(defaultValues);
      modeResetRequiredRef.current = false;
      if (nameToPreserve !== undefined) {
        methods.setValue("name", nameToPreserve, { shouldDirty: true });
      }
      applyFinalDraftDefaultsRef.current(true);
    }
  }, [defaultValues, isActive, methods.reset, isCreatePending, mode]);

  const handleSelectSource = useCallback(
    (next: ProjectCreateSource, previousSource?: ProjectCreateSource) => {
      trackEvent(ANALYTICS_EVENTS.PROJECT_CREATE_SOURCE_SELECTED, {
        source: next,
        mode,
        surface: "sheet",
        ...(previousSource && { previous_source: previousSource }),
      });
      if (mode === "project" && entrySource === "onboarding") {
        trackEvent(ANALYTICS_EVENTS.PROJECT_SETUP_SOURCE_SELECTED, {
          source: next,
          entry_source: entrySource,
          workspace_id: workspaceId,
        });
        patchPreferences?.({
          onboarding: {
            dismissed: false,
            projectSetupStatus: "source_selected",
            projectSetupSource: next,
            entrySource,
          },
        });
      }
      setSource(next);
      setStage("form");
    },
    [entrySource, mode, patchPreferences, trackEvent, workspaceId]
  );

  // Users without a screenplay used to stall here on a disabled Create button.
  const handleStartBlankInstead = useCallback(() => {
    carriedNameRef.current = methods.getValues("name")?.trim() || null;
    resetFileState();
    handleSelectSource("blank", "import");
  }, [handleSelectSource, methods, resetFileState]);

  const handleValidateAndSubmit = async () => {
    const isValid = await methods.trigger();
    if (!isValid) {
      const invalidFields = Object.keys(methods.formState.errors);
      trackEvent(ANALYTICS_EVENTS.PROJECT_CREATE_VALIDATION_BLOCKED, {
        fields: invalidFields,
        mode,
        source: source ?? (isVersionMode ? "import" : null),
      });
      handleToast("Fix the highlighted fields to continue.", "error");
      const firstInvalid = invalidFields[0] as keyof FormData | undefined;
      if (firstInvalid) {
        methods.setFocus(firstInvalid);
      }
      return;
    }
    const values = methods.getValues();
    submitForm(values);
  };

  const blockSubmit = (reason: string, message: string) => {
    trackEvent(ANALYTICS_EVENTS.PROJECT_CREATE_BLOCKED, { reason, mode });
    handleToast(message, "error");
  };

  const submitForm = (data: FormData) => {
    const resolvedSource: ProjectCreateSource = isVersionMode ? "import" : source || "import";
    if (resolvedSource === "import" && !file) {
      blockSubmit("missing_file", "Attach a screenplay before continuing.");
      return;
    }
    if (mode === "project" && !canCreateProject) {
      blockSubmit(
        "no_permission",
        "You do not have permission to create a project in this workspace."
      );
      return;
    }

    const taskId = startCreateProject(
      {
        formData: data,
        file: resolvedSource === "blank" ? null : file,
        parsedData: resolvedSource === "blank" ? null : (parsedData as never),
        mode,
        existingProject,
        source: resolvedSource,
        workspaceId: workspaceId ?? undefined,
      },
      {
        onSuccess: (project) => {
          const completedAt = new Date().toISOString();
          if (mode === "project" && entrySource === "onboarding") {
            patchPreferences?.({
              onboarding: {
                dismissed: false,
                projectSetupStatus:
                  resolvedSource === "import" ? "screenplay_ready" : "project_created",
                projectSetupSource: resolvedSource,
                entrySource,
                projectId: String(project.project_id),
                projectCreatedAt: completedAt,
                ...(resolvedSource === "import" && { screenplayReadyAt: completedAt }),
              },
            });
          }
          if (resolvedSource === "import") {
            trackEvent(ANALYTICS_EVENTS.SCREENPLAY_ADDED, {
              project_id: String(project.project_id),
              workspace_id: workspaceId,
              source: "import",
            });
          }
          onSuccess?.(project);
          onClose();
        },
      }
    );
    if (taskId) {
      setActiveTaskId(taskId);
      return;
    }
    // startCreateProject already toasted the reason; record it so the funnel sees the drop-off
    // instead of an abandonment with no explanation.
    trackEvent(ANALYTICS_EVENTS.PROJECT_CREATE_BLOCKED, { reason: "task_lock", mode });
  };

  const needsFile = isVersionMode || source === "import";
  const awaitingFile = needsFile && !file;
  const primaryDisabled = isSubmitting || isCreatePending || awaitingFile;

  const sheetTitle = mode === "version" ? "Create New Version" : "Create New Project";
  const detailsLayout: "minimal" | "full" =
    isVersionMode || source === "import" ? "full" : "minimal";

  const sheetDescription = isCreatePending
    ? mode === "version"
      ? "Setting up your new version"
      : "Setting up your new project"
    : mode === "version"
      ? "Create a new version with updated screenplay"
      : stage === "source"
        ? "Choose how you want to start"
        : source === "blank"
          ? "Add a name to create your project. You can adjust details anytime in project settings."
          : otpDraftWithoutAvailableLicense
            ? "Add your screenplay and a name."
            : "Set up your project and features";

  if (!isActive) {
    return null;
  }

  if (permissions.isLoading && !isCreatePending) {
    return <LoadingState isOpen={isActive} onClose={handleRequestClose} />;
  }

  if (!canCreateProject && mode === "project" && !isCreatePending) {
    if (isGateRedirect) {
      return null;
    }
    return (
      <PermissionDeniedState
        isOpen={isActive}
        onClose={handleRequestClose}
        permissions={permissions}
      />
    );
  }

  const stageText = activeTask
    ? activeTask.stage === "uploading" && typeof activeTask.uploadProgress === "number"
      ? `Uploading your screenplay... ${Math.round(activeTask.uploadProgress * 100)}%`
      : (stageTexts[activeTask.stage] ?? "Working on it...")
    : "Working on it...";

  const creatingBody = (
    <div className="flex-1 p-6 flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4 mx-auto">
          <FileText className="w-6 h-6 text-primary" />
        </div>
        <h4 className="text-sm font-medium text-foreground mb-2">
          {mode === "version" ? "Creating New Version" : "Creating New Project"}
        </h4>
        <LoaderV2
          mode={LoaderMode.inline}
          theme={LoaderTheme.brand}
          size="sm"
          rotatingTexts={[stageText]}
          textInterval={2000}
          textClassName="text-muted-foreground text-xs"
        />
        <p className="mt-4 text-xs text-muted-foreground">
          You can keep working while this finishes.
        </p>
        <Button
          type="button"
          variant="ghostSecondary"
          color="gray"
          className="mt-3"
          onClick={continueInBackground}
        >
          Continue in background
        </Button>
      </div>
    </div>
  );

  const footer = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {stage === "form" && !isCreatePending && awaitingFile ? (
        // Visible because the disabled button cannot show its title tooltip.
        <span
          id="create-project-primary-hint"
          className="mr-auto text-xs text-muted-foreground"
          aria-live="polite"
        >
          Attach a screenplay to continue.
        </span>
      ) : null}
      <Button
        type="button"
        variant="ghostSecondary"
        color="gray"
        onClick={handleRequestClose}
        disabled={isSubmitting || isCreatePending}
      >
        Cancel
      </Button>
      {stage === "form" && !isCreatePending ? (
        <Button
          type="submit"
          disabled={primaryDisabled}
          isLoading={isSubmitting || isCreatePending}
          onClick={handleValidateAndSubmit}
          variant="ghostBrand"
          color="brand"
          title={awaitingFile ? "Attach a screenplay to continue." : undefined}
          aria-describedby={awaitingFile ? "create-project-primary-hint" : undefined}
        >
          {mode === "version" ? "Create Version" : "Create Project"}
        </Button>
      ) : null}
    </div>
  );

  const body = isCreatePending ? (
    creatingBody
  ) : (
    <FormProvider {...methods}>
      <div className="flex-1 p-6">
        <PermissionAlerts
          permissions={permissions}
          canCreateProject={canCreateProject}
          mode={mode}
        />

        {stage === "source" && !isVersionMode ? (
          <SourcePicker onSelect={handleSelectSource} />
        ) : (
          <DetailsStage
            control={control}
            errors={errors}
            mode={mode}
            layout={detailsLayout}
            otpDraftWithoutAvailableLicense={otpDraftWithoutAvailableLicense}
            finalDraftTagCount={finalDraftTagCount}
            afterDetails={
              isVersionMode || source === "import" ? (
                <ImportStage
                  file={file}
                  fileLoading={fileLoading}
                  fileError={fileError}
                  handleFileUpload={handleFileUpload}
                  mode={mode}
                  existingProject={existingProject}
                  onStartBlank={isVersionMode ? undefined : handleStartBlankInstead}
                  finalDraftTags={finalDraftTags}
                  heldOffFeatures={hasScriptBreakdown ? null : heldOffFeatures}
                />
              ) : undefined
            }
          />
        )}
      </div>
    </FormProvider>
  );

  if (stage === "source" && !isVersionMode && !isCreatePending) {
    return (
      <Modal isOpen={isActive} onClose={handleRequestClose} maxWidth="max-w-2xl">
        <ModalHeader title={sheetTitle} subtitle={sheetDescription} onClose={handleRequestClose} />
        <FormProvider {...methods}>
          <div className="flex flex-col gap-4 pt-2">
            <PermissionAlerts
              permissions={permissions}
              canCreateProject={canCreateProject}
              mode={mode}
            />
            <SourcePicker onSelect={handleSelectSource} />
          </div>
        </FormProvider>
        {footer}
      </Modal>
    );
  }

  return (
    <ModernSheet
      isOpen={isActive}
      onClose={handleRequestClose}
      title={
        isCreatePending
          ? mode === "version"
            ? "Creating Version..."
            : "Creating Project..."
          : sheetTitle
      }
      description={sheetDescription}
      size="xl"
      className="max-h-screen max-w-[100vw]"
      footer={isCreatePending ? undefined : footer}
    >
      {body}
    </ModernSheet>
  );
};
